/**
 * Per-step display metadata for the deploy-step cards: a friendly TITLE, a specific
 * ARTWORK image (served from `public/assets/brand-deployment/steps/`), and a
 * PRIORITY, keyed by the RAW pipeline step name the deployment emits in
 * `status_details.steps` (e.g. "prepare_logo_assets", "evergreen:Drops&Wins",
 * "gamification:WHEEL:LEVEL_3").
 *
 * `priority` (1..N, ascending) records the canonical DEPLOY ORDER (provisioning first,
 * gamification reward tiers last). It is REFERENCE DATA, not an active sort: nothing reads
 * it today. The carousel's real order comes from `buildTimeline`, which groups by state
 * (completed by timestamp, then failed, skipped, active, pending), so that a live swap to
 * `status_details.steps` can never reorder cards under the viewer. Keep the numbers
 * contiguous when adding a step, so the column stays usable as documentation.
 *
 * The catalog mirrors the steps returned by the real `casino-instance/list` API (the
 * same source the Casino-Settings "Technical details" page reads).
 *
 * KEYING. Core + `evergreen:*` steps are keyed on their exact raw name — the API's
 * stable identity. `gamification:*` steps are `gamification:<CLASS>:<TIER>` where the
 * TIER (`LEVEL_1`/`LEVEL_2`/`LEVEL_3`) is the brand's PURCHASED commercial tier and so
 * varies brand-to-brand; we therefore key those by CLASS only (`gamification:WHEEL`),
 * collapsing the tier via {@link stepMetaKey}. That mirrors {@link humanizeStep}, which
 * already ignores the tier, so a brand that buys a mechanic at a different level still
 * resolves to the same card instead of falling through to the placeholder.
 *
 * The card + summary modal NEVER show the raw key, only `resolveStepMeta(raw).title`,
 * so an UNMAPPED step degrades gracefully (humanized name + generic artwork + last).
 * Adding a mapping is purely additive: drop the image in the folder and add a row here.
 *
 * The titles are rendered through <Text> (display copy treated as data, like the raw
 * names). To localise them, add the English title as a key in every locale.
 */

import type { ResolvedMode } from '@/nova/theme/themeConfig';

/** Friendly labels for the content-pipeline steps most likely to stall (keeps raw,
 *  technical step names like `crm_bootstrap` out of the UI). Unmapped steps fall
 *  back to a prettified form (see {@link humanizeStep}). This is the SHORT label used
 *  by the Casino-Settings "Technical details" verbose list; the deploy-strip CARD uses
 *  the fuller `STEP_META[...].title` instead. */
const STEP_LABELS: Record<string, string> = {
  crm_bootstrap: 'CRM',
  setup_lumio: 'Lumio',
  provision_email_domain: 'Email domain',
  prepare_logo_assets: 'Logos',
  generate_notifications: 'Notifications',
  generate_tone_of_voice: 'Tone of voice',
  create_welcome_bonus: 'Welcome bonus',
  create_above_the_fold_hero: 'Homepage hero',
  ingest_lumio_documents: 'Knowledge base',
  create_art_info: 'Artwork',
  create_variable_defaults: 'Theme',
  check_casino_exists: 'Provisioning',
};

const prettify = (s: string): string =>
  s
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

/** `crm_bootstrap` → "CRM"; `gamification:AIR_TIMER:LEVEL_2` → "Air Timer"; `evergreen:Drops&Wins` → "Drops&Wins".
 *  Exported so the Casino-Settings "Technical details" verbose deployment list (the single
 *  place this site's per-step progress is shown) labels its real pipeline steps cleanly, and
 *  reused as the graceful fallback title for any step this catalog does not map. */
export const humanizeStep = (raw: string): string => {
  if (STEP_LABELS[raw]) return STEP_LABELS[raw];
  if (raw.includes(':')) {
    const parts = raw.split(':');
    return prettify(parts.length >= 3 ? parts[1] : parts[parts.length - 1]);
  }
  return prettify(raw);
};

export type StepMeta = {
  /** Friendly card title shown instead of the raw step name. */
  title: string;
  /** Card background artwork (absolute public path). */
  artwork: string;
  /** Canonical deploy order (1 = first), for reference. Not read by the carousel, which
   *  orders by step STATE in `buildTimeline`. See this module's header. */
  priority: number;
};

/** Base dir every deploy-step artwork lives in (drop the image files named below into it). */
const STEPS_DIR = '/assets/brand-deployment/steps';

/** Generic artwork used as the fallback for any UNMAPPED step. */
export const DEFAULT_STEP_ARTWORK = `${STEPS_DIR}/stepsPlaceholder.jpg`;

/**
 * Suffix that names the LIGHT-theme twin of an artwork. The dark files below are the
 * canonical names; the light set is the same names carrying this suffix, so a new step
 * needs no second table (`step-logo.jpg` plus `step-logo-light.jpg`).
 */
const LIGHT_SUFFIX = '-light';

/** Matches a trailing file extension, ignoring dots that appear in directory names. */
const EXTENSION_RE = /(\.[^./]+)$/;

/**
 * Light-theme counterpart of an artwork path: the same file name with {@link LIGHT_SUFFIX}
 * inserted before the extension. A path with no extension simply gets the suffix appended,
 * so it can never silently resolve back to the dark file.
 */
export const lightArtwork = (artwork: string): string =>
  EXTENSION_RE.test(artwork) ? artwork.replace(EXTENSION_RE, `${LIGHT_SUFFIX}$1`) : `${artwork}${LIGHT_SUFFIX}`;

/** Light-theme placeholder, i.e. the generic fallback under the same naming rule. */
export const DEFAULT_STEP_ARTWORK_LIGHT = lightArtwork(DEFAULT_STEP_ARTWORK);

/**
 * ONE artwork per step. Each points at a file in `public/assets/brand-deployment/steps/` — the file
 * NAME is the last path segment. All of the DARK plates below are shipped. A missing file no longer
 * shows an empty image: `stepArtworkChain` walks to the placeholder, so a gap degrades quietly rather
 * than breaking the card. Adding a brand-new step is additive: add a const here + its STEP_META row
 * + drop BOTH the dark file and its `-light` twin in the folder.
 *
 * The names below are the DARK set. Every one of them also has a light twin named with the
 * `-light` suffix (see {@link lightArtwork}), which the strip uses when the studio theme
 * resolves to light; {@link stepArtworkChain} owns the per-theme lookup and its fallbacks.
 * Both sets are 394x700 JPEGs in the 24-44KB band; keep a new plate inside that envelope so
 * the strip's cards stay cheap to load. The per-row comments record which files exist, so
 * keep them honest.
 */
// Core provisioning + Lumio setup
const CHECK_CASINO_ARTWORK = `${STEPS_DIR}/step-provisioning.jpg`; // shipped
const CRM_ARTWORK = `${STEPS_DIR}/step-crm.jpg`; // shipped
const LUMIO_ARTWORK = `${STEPS_DIR}/step-lumio.jpg`; // shipped
const EMAIL_DOMAIN_ARTWORK = `${STEPS_DIR}/step-email-domain.jpg`; // shipped
const KNOWLEDGE_BASE_ARTWORK = `${STEPS_DIR}/step-knowledge-base.jpg`; // shipped
const TONE_OF_VOICE_ARTWORK = `${STEPS_DIR}/step-tone-of-voice.jpg`; // shipped

// Brand assets + content
const LOGO_ARTWORK = `${STEPS_DIR}/step-logo.jpg`; // shipped
const ART_INFO_ARTWORK = `${STEPS_DIR}/step-artwork.jpg`; // shipped
const SYSTEM_ART_BUNDLE_ARTWORK = `${STEPS_DIR}/step-system-art-bundles.jpg`; // shipped
const HERO_ARTWORK = `${STEPS_DIR}/step-hero.jpg`; // shipped
const THEME_ARTWORK = `${STEPS_DIR}/step-theme.jpg`; // shipped
const WELCOME_BONUS_ARTWORK = `${STEPS_DIR}/step-welcome-bonus.jpg`; // shipped
const NOTIFICATIONS_ARTWORK = `${STEPS_DIR}/step-notifications.jpg`; // shipped

// Evergreen mechanics
const CALENDAR_ARTWORK = `${STEPS_DIR}/step-calendar.jpg`; // shipped
const CHALLENGES_ARTWORK = `${STEPS_DIR}/step-challenges.jpg`; // shipped
const DROPS_WINS_ARTWORK = `${STEPS_DIR}/step-drops-and-wins.jpg`; // shipped
const HAPPY_HOUR_ARTWORK = `${STEPS_DIR}/step-happy-hour.jpg`; // shipped
const AIRTIMER_ARTWORK = `${STEPS_DIR}/step-airtimer.jpg`; // shipped
const TOURNAMENT_ARTWORK = `${STEPS_DIR}/step-tournament.jpg`; // shipped
const PLAYTECH_LOYALTY_ARTWORK = `${STEPS_DIR}/step-playtech-loyalty.jpg`; // shipped
const WAZDAN_CASH_DROP_ARTWORK = `${STEPS_DIR}/step-wazdan-cash-drop.jpg`; // shipped

// Gamification reward tiers
const WHEEL_ARTWORK = `${STEPS_DIR}/step-wheel.jpg`; // shipped
// NOTE: `step-wheel-reward.jpg` (+ its -light twin) is also in the folder but deliberately
// unmapped. It is staged for a future dedicated wheel-REWARD step; until that step exists,
// `gamification:WHEEL` keeps the plate above. Not an oversight, do not "fix" by rewiring.
const CALENDAR_REWARD_ARTWORK = `${STEPS_DIR}/step-calendar-reward.jpg`; // shipped
const HAPPY_HOUR_REWARD_ARTWORK = `${STEPS_DIR}/step-happy-hour-reward.jpg`; // shipped
const TOURNAMENT_REWARD_ARTWORK = `${STEPS_DIR}/step-tournament-reward.jpg`; // shipped
const JACKPOT_ARTWORK = `${STEPS_DIR}/step-jackpot.jpg`; // shipped

/**
 * Raw pipeline key for the generic system art step. Confirmed against the live pipeline:
 * the trailing segment is PLURAL (`…_bundles`), matching the plate's file name, while the
 * card's title stays singular because it reads better.
 *
 * Kept as a named constant because it is the one key whose spelling was not read straight
 * off a `casino-instance/list` response, so the test next door pins it explicitly rather
 * than leaving it buried in the table below.
 */
export const SYSTEM_ART_BUNDLE_STEP = 'create_generic_system_art_bundles';

/** An unmapped step sorts after every known one (and titles itself with its humanized name). */
const DEFAULT_STEP_PRIORITY = 999;

/**
 * Friendly title + artwork + deploy-order priority per step key. Sourced from the live
 * `casino-instance/list` response (`status_details.steps`): the core provisioning / Lumio
 * pipeline (1-12), the `evergreen:*` mechanics (13-20), then the `gamification:*` reward
 * tiers (21-27, keyed by CLASS — see {@link stepMetaKey}).
 */
const STEP_META: Record<string, StepMeta> = {
  // Core provisioning + Lumio setup
  check_casino_exists: { title: 'Provisioning your casino', artwork: CHECK_CASINO_ARTWORK, priority: 1 },
  crm_bootstrap: { title: 'Bootstrapping your CRM', artwork: CRM_ARTWORK, priority: 2 },
  setup_lumio: { title: 'Setting up Lumio assistant', artwork: LUMIO_ARTWORK, priority: 3 },
  provision_email_domain: { title: 'Provisioning your email domain', artwork: EMAIL_DOMAIN_ARTWORK, priority: 4 },
  ingest_lumio_documents: { title: 'Building your knowledge base', artwork: KNOWLEDGE_BASE_ARTWORK, priority: 5 },
  generate_tone_of_voice: { title: 'Tuning your tone of voice', artwork: TONE_OF_VOICE_ARTWORK, priority: 6 },

  // Brand assets + content
  prepare_logo_assets: { title: 'Applying brand assets', artwork: LOGO_ARTWORK, priority: 7 },
  create_art_info: { title: 'Generating artwork', artwork: ART_INFO_ARTWORK, priority: 8 },
  // The generic system art set, kept next to the brand-artwork step it sits alongside in
  // the pipeline. Keyed off the named constant above, whose spelling is plural.
  [SYSTEM_ART_BUNDLE_STEP]: {
    title: 'Creating the system art bundle',
    artwork: SYSTEM_ART_BUNDLE_ARTWORK,
    priority: 9,
  },
  create_above_the_fold_hero: { title: 'Building your homepage hero', artwork: HERO_ARTWORK, priority: 10 },
  create_variable_defaults: { title: 'Building your theme', artwork: THEME_ARTWORK, priority: 11 },
  create_welcome_bonus: { title: 'Creating your welcome bonus', artwork: WELCOME_BONUS_ARTWORK, priority: 12 },
  generate_notifications: { title: 'Configuring notifications', artwork: NOTIFICATIONS_ARTWORK, priority: 13 },

  // Evergreen mechanics
  'evergreen:Calendar': { title: 'Enabling Calendar', artwork: CALENDAR_ARTWORK, priority: 14 },
  'evergreen:Challenge': { title: 'Enabling Challenges', artwork: CHALLENGES_ARTWORK, priority: 15 },
  'evergreen:Drops&Wins': { title: 'Enabling Drops & Wins', artwork: DROPS_WINS_ARTWORK, priority: 16 },
  'evergreen:Happy Hour': { title: 'Enabling Happy Hour', artwork: HAPPY_HOUR_ARTWORK, priority: 17 },
  'evergreen:AirTimer': { title: 'Enabling Air Timer', artwork: AIRTIMER_ARTWORK, priority: 18 },
  'evergreen:Tournament': { title: 'Enabling Tournaments', artwork: TOURNAMENT_ARTWORK, priority: 19 },
  'evergreen:Playtech Loyalty': { title: 'Enabling Playtech Loyalty', artwork: PLAYTECH_LOYALTY_ARTWORK, priority: 20 },
  'evergreen:Wazdan Cash Drop': {
    title: 'Launching Wazdan Cash Drop',
    artwork: WAZDAN_CASH_DROP_ARTWORK,
    priority: 21,
  },

  // Gamification reward tiers — keyed by CLASS (tier collapsed via stepMetaKey)
  'gamification:WHEEL': { title: 'Building the prize wheel', artwork: WHEEL_ARTWORK, priority: 22 },
  'gamification:CALENDAR': {
    title: 'Configuring the calendar reward',
    artwork: CALENDAR_REWARD_ARTWORK,
    priority: 23,
  },
  'gamification:HAPPY_HOUR': {
    title: 'Configuring Happy Hour rewards',
    artwork: HAPPY_HOUR_REWARD_ARTWORK,
    priority: 24,
  },
  'gamification:JACKPOT': { title: 'Setting up the jackpot', artwork: JACKPOT_ARTWORK, priority: 25 },
  'gamification:AIR_TIMER': { title: 'Configuring Air Timer rewards', artwork: AIRTIMER_ARTWORK, priority: 26 },
  'gamification:TOURNAMENTS': { title: 'Configuring tournament rewards', artwork: TOURNAMENT_REWARD_ARTWORK, priority: 27 },
  'gamification:CHALLENGE': { title: 'Configuring challenge rewards', artwork: CHALLENGES_ARTWORK, priority: 28 },
};

/** Prefix every `gamification:<CLASS>:<TIER>` step shares. */
const GAMIFICATION_PREFIX = 'gamification:';

/**
 * Collapse a raw pipeline step to its {@link STEP_META} key. Gamification steps drop the
 * trailing commercial tier (`gamification:AIR_TIMER:LEVEL_1` → `gamification:AIR_TIMER`) so
 * every tier of a class resolves to the same card; every other step is already its own key.
 */
const stepMetaKey = (rawStep: string): string => {
  if (!rawStep.startsWith(GAMIFICATION_PREFIX)) return rawStep;
  const [, klass] = rawStep.split(':');
  return klass ? `${GAMIFICATION_PREFIX}${klass}` : rawStep;
};

/**
 * Resolve a raw pipeline step to its title + artwork + priority. Gamification steps match by
 * CLASS (tier-agnostic); core + evergreen steps match on their exact raw name. Each mapped step
 * has its OWN dedicated artwork const (see above); once the matching image file exists in the
 * folder it renders that art. An UNMAPPED step falls back to its humanized name + the
 * stepsPlaceholder artwork, sorted last.
 */
export const resolveStepMeta = (rawStep: string): StepMeta =>
  STEP_META[stepMetaKey(rawStep)] ?? {
    title: humanizeStep(rawStep),
    artwork: DEFAULT_STEP_ARTWORK,
    priority: DEFAULT_STEP_PRIORITY,
  };

/**
 * The artwork candidates for a step in the resolved theme, best first: the card tries each
 * in turn and keeps the first that loads (see BounceCard).
 *
 * On light, a step whose `-light` file is not uploaded yet drops to the LIGHT PLACEHOLDER
 * rather than to its own dark artwork, so the strip never mixes dark plates into the light
 * page. The dark placeholder closes both chains purely as a never-blank last resort, for
 * the window before the light files land. Duplicates are collapsed, so an unmapped step
 * (whose own artwork already IS the placeholder) does not list it twice.
 */
export const stepArtworkChain = (rawStep: string, mode: ResolvedMode): readonly string[] => {
  const { artwork } = resolveStepMeta(rawStep);
  const candidates =
    mode === 'light'
      ? [lightArtwork(artwork), DEFAULT_STEP_ARTWORK_LIGHT, DEFAULT_STEP_ARTWORK]
      : [artwork, DEFAULT_STEP_ARTWORK];
  return candidates.filter((path, index) => candidates.indexOf(path) === index);
};

export default resolveStepMeta;
